import { useEffect, useState, type ComponentType } from 'react'
import { ChevronLeft, ChevronRight, Pause, Play, RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { litParts, type FlowStep } from '@/help/architecture/flows'
import { DiagramViewer } from './DiagramViewer'

const STEP_MS = 2800

interface Props {
  title: string
  steps: readonly FlowStep[]
  Diagram: ComponentType<{ lit?: ReadonlySet<string> | null }>
}

/**
 * A diagram whose order matters, walked one step at a time: each step lights
 * its parts and dims the rest. With no step chosen it is the plain diagram.
 */
export function FlowStepper({ title, steps, Diagram }: Props) {
  const [step, setStep] = useState<number | null>(null)
  const [playing, setPlaying] = useState(false)
  const last = steps.length - 1

  useEffect(() => {
    if (!playing) return
    const id = setTimeout(() => {
      if (step === last) setPlaying(false)
      else setStep(s => (s === null ? 0 : s + 1))
    }, step === null ? 0 : STEP_MS)
    return () => clearTimeout(id)
  }, [playing, step, last])

  const go = (n: number | null) => {
    setPlaying(false)
    setStep(n)
  }
  const play = () => {
    if (playing) return setPlaying(false)
    if (step === last) setStep(null)
    setPlaying(true)
  }
  const lit = litParts(steps, step)
  const btn = 'flex h-7 items-center justify-center gap-1 rounded-md border px-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40'

  const controls = (
    <div
      className='flex flex-wrap items-center gap-1.5'
      role='group'
      aria-label={`Step through: ${title}`}
      onKeyDown={e => {
        if (e.key === 'ArrowRight') go(step === null ? 0 : Math.min(step + 1, last))
        if (e.key === 'ArrowLeft') go(step === null ? 0 : Math.max(step - 1, 0))
        if (e.key === 'Escape') go(null)
      }}
    >
      <button type='button' onClick={play} className={cn(btn, 'text-foreground')} aria-label={playing ? 'Pause' : 'Play the flow'}>
        {playing ? <Pause className='h-3.5 w-3.5' /> : <Play className='h-3.5 w-3.5' />}
        {playing ? 'Pause' : 'Play'}
      </button>
      <button type='button' onClick={() => go(step === null ? 0 : step - 1)} disabled={step === null || step === 0} className={btn} aria-label='Previous step'>
        <ChevronLeft className='h-3.5 w-3.5' />
      </button>
      {/* !-prefixed: the wiki's prose list styling would number these a second time. */}
      <ol className='!mb-0 flex items-center gap-1 !pl-0 !list-none'>
        {steps.map((s, i) => (
          <li key={s.title} className='!mb-0'>
            <button
              type='button'
              onClick={() => go(i)}
              aria-current={step === i ? 'step' : undefined}
              title={s.title}
              aria-label={`Step ${i + 1}: ${s.title}`}
              className={cn(
                'flex h-7 min-w-7 items-center justify-center rounded-full px-1.5 text-[11px] font-semibold tabular-nums transition-colors',
                step === i
                  ? 'bg-primary text-primary-foreground'
                  : step !== null && i < step
                    ? 'bg-primary/15 text-primary'
                    : 'bg-muted text-muted-foreground hover:bg-muted/70 hover:text-foreground',
              )}
            >
              {i + 1}
            </button>
          </li>
        ))}
      </ol>
      <button type='button' onClick={() => go(step === null ? 0 : step + 1)} disabled={step === last} className={btn} aria-label='Next step'>
        <ChevronRight className='h-3.5 w-3.5' />
      </button>
      {step !== null && (
        <button type='button' onClick={() => go(null)} className={cn(btn, 'ml-auto')} title='Show the whole diagram (Esc)'>
          <RotateCcw className='h-3 w-3' />
          Show all
        </button>
      )}
    </div>
  )

  const current = step === null ? null : steps[step]
  const panel = (
    <div className='px-4 py-3 text-sm' aria-live='polite'>
      {current ? (
        <>
          <div className='font-semibold text-foreground'>
            <span className='mr-2 text-xs font-semibold text-primary'>
              Step {step! + 1} of {steps.length}
            </span>
            {current.title}
          </div>
          <div className='mt-0.5 text-muted-foreground'>{current.detail}</div>
        </>
      ) : (
        <div className='text-muted-foreground'>
          Press <span className='font-medium text-foreground'>Play</span> or pick a step to walk the flow in order. Arrow keys move between steps.
        </div>
      )}
    </div>
  )

  return (
    <DiagramViewer title={title} controls={controls} panel={panel}>
      <Diagram lit={lit} />
    </DiagramViewer>
  )
}
